// MA-1004 (operator rulings R1, R2; MA-790's resume-on-reading-foundation-mixed
// slice and probe (a)) — capture targeting on the session routes.
//
// R1: /play/reading and /play/mixed take the same validated `seed` and
// `resume` search params as /play/session, with the same validation (the
// parsers below; play.session.tsx uses them too), used only when no session
// checkpoint exists. A real child never carries them, so with neither param
// every value below equals the screen's pre-MA-1004 expression (pinned in
// test:ma1004-session-deep-link).
//
// R2: each session screen exposes the served slot's role as data-slot-role,
// one of the plan's own role values (SESSION_RUN_ROLES, progression.ts).
import { SESSION_RUN_ROLES, type SessionRun, type SessionRunRole } from "@/lib/engine/progression";

/** MA-790 (f) — the shape every seed a session screen mints has (base36 of
 *  Math.random() plus base36 of Date.now(), lowercase letters and digits). A
 *  `seed` search value is honoured only in exactly this shape; anything else
 *  is dropped (never throws), the same way an invalid `resume` is. */
export const SESSION_SEED_RE = /^[a-z0-9]{8,40}$/;

/** A `resume` search value: a non-negative integer, else undefined. */
export function parseResumeParam(v: unknown): number | undefined {
  const r = Number(v);
  return Number.isInteger(r) && r >= 0 ? r : undefined;
}

/** A `seed` search value in the minted shape, else undefined. */
export function parseSeedParam(v: unknown): string | undefined {
  return typeof v === "string" && SESSION_SEED_RE.test(v) ? v : undefined;
}

/** The session seed a mount uses: a checkpoint's seed, else the validated
 *  `seed` param (only when no checkpoint exists), else a fresh one. */
export function sessionSeedFor(checkpointSeed: string | undefined, seedParam: string | undefined, fresh: () => string): string {
  return checkpointSeed ?? seedParam ?? fresh();
}

/** The cursor a Reading or Mixed mount starts at: a checkpoint's own cursor
 *  (`done ?? 0`, exactly as before) when one exists, else the validated
 *  `resume` index, else 0. */
export function deepLinkStartIndex(checkpoint: { done?: number } | null | undefined, resumeAt: number | undefined): number {
  if (checkpoint) return checkpoint.done ?? 0;
  return resumeAt ?? 0;
}

/** MA-1004 RED r1 S1 (ruled required) — may the on-cursor checkpoint save
 *  run? The old rule (`done !== 0` and the session not finished), and never
 *  while a deep link's clamp is pending: a past-plan index must never be
 *  saved, or a later plain mount would resume past the plan and MA-753's
 *  finalize would credit a completion no answer earned. With no deep link
 *  (pending false) this is exactly the old condition. */
export function mayPersistCursor(done: number, sessionDone: boolean, deepLinkClampPending: boolean): boolean {
  return done !== 0 && !sessionDone && !deepLinkClampPending;
}

/** True only for a capture-style deep link: no checkpoint and a `resume`. */
export function isDeepLinkResume(checkpoint: unknown, resumeAt: number | undefined): boolean {
  return !checkpoint && typeof resumeAt === "number";
}

/** The served slot's role for data-slot-role ("" when the run has no slot
 *  there). Math and Reading: the run's own role at the cursor (what
 *  Reading's attempts log as adaptiveReason). */
export function slotRoleFor(run: Pick<SessionRun, "questions"> | null | undefined, done: number): SessionRunRole | "" {
  const role = run?.questions?.[done]?.role;
  return role && (SESSION_RUN_ROLES as readonly string[]).includes(role) ? role : "";
}

/** MA-1004 (R1) — Mixed's data-run-plan-ids, "the same way" as Math and
 *  Reading: the item a FRESH mount resumed at index k serves at k (no answer
 *  in this run yet), i.e. `probe.slotAt(k, [])` of a stepper built with the
 *  live one's inputs; the frozen plan's slot where the probe has no pick, or
 *  with no stepper at all (an unarmed run serves the frozen plan). */
export function mixedPlanProbeIds<T extends { id: string }>(
  plan: { slots: ReadonlyArray<{ item: T }> },
  probe: { slotAt(done: number, log: readonly never[]): { item: T } | null } | null,
): string[] {
  return plan.slots.map((slot, k) => (probe ? probe.slotAt(k, [])?.item.id : undefined) ?? slot.item.id);
}

/** Mixed: every slot is a cross-strand slot (buildMixedSession roles every
 *  question cross_strand; its attempts log adaptiveReason "cross_strand"). */
export const MIXED_SLOT_ROLE: SessionRunRole = "cross_strand";
/** Foundation: no SessionRun (single-skill by design, P100); every slot
 *  serves the focal skill. */
export const FOUNDATION_SLOT_ROLE: SessionRunRole = "focal";
